import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import ProfileCard from "@/components/ProfileCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FunnelSimple, MagnifyingGlass } from "@phosphor-icons/react";

const emptyFilters = {
  gender: "", community: "", mother_tongue: "",
  city: "", profession: "", marital_status: "", diet: "",
  age_min: "", age_max: "", q: "",
};

const SECTS = ["Sunni", "Shia", "Sufi", "Bohra", "Ismaili", "Ahmadiyya", "Other"];
const MOTHER_TONGUES = ["Urdu", "Arabic", "Hindi", "English", "Bengali", "Pashto", "Farsi", "Malayalam", "Tamil", "Kashmiri", "Gujarati", "Marathi"];

export default function Browse() {
  const [filters, setFilters] = useState(emptyFilters);
  const [profiles, setProfiles] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const load = async (p = 1, f = filters) => {
    setLoading(true);
    try {
      const params = { page: p, limit: 12 };
      for (const [k, v] of Object.entries(f)) if (v) params[k] = v;
      const { data } = await api.get("/profiles", { params });
      setProfiles(data.profiles || []);
      setTotal(data.total || 0);
      setPage(data.page || 1);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(1, emptyFilters); }, []);

  const upd = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const apply = () => load(1, filters);
  const reset = () => { setFilters(emptyFilters); load(1, emptyFilters); };

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-12" data-testid="browse-page">
      <div>
        <div className="label-eyebrow text-brand-terracotta">Discover</div>
        <h1 className="font-serif text-5xl md:text-6xl tracking-tighter mt-2 text-brand-ink">Browse profiles</h1>
        <p className="mt-3 text-brand-muted"><span className="num-lining">{total}</span> profile{total === 1 ? "" : "s"} match your criteria</p>
      </div>

      <div className="mt-8 grid grid-cols-12 gap-6">
        <aside className="col-span-12 lg:col-span-3 bg-white border border-brand-line p-6 h-fit lg:sticky lg:top-24" data-testid="browse-filters">
          <div className="flex items-center gap-2 mb-6">
            <FunnelSimple size={18} className="text-brand-terracotta" />
            <div className="label-eyebrow text-brand-muted">Filters</div>
          </div>

          <div className="space-y-5">
            <div>
              <Label className="text-xs text-brand-muted">Search</Label>
              <div className="mt-1.5 relative">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
                <Input value={filters.q} onChange={(e) => upd("q", e.target.value)} data-testid="filter-search" className="pl-9 h-10 rounded-none border-brand-line" placeholder="Name, city, profession…" />
              </div>
            </div>

            <div>
              <Label className="text-xs text-brand-muted">Looking for</Label>
              <Select value={filters.gender} onValueChange={(v) => upd("gender", v === "any" ? "" : v)}>
                <SelectTrigger data-testid="filter-gender" className="mt-1.5 h-10 rounded-none border-brand-line">
                  <SelectValue placeholder="Any" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  <SelectItem value="male">Brother</SelectItem>
                  <SelectItem value="female">Sister</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-brand-muted">Age from</Label>
                <Input type="number" value={filters.age_min} onChange={(e) => upd("age_min", e.target.value)} data-testid="filter-age-min" className="mt-1.5 h-10 rounded-none border-brand-line" placeholder="18" />
              </div>
              <div>
                <Label className="text-xs text-brand-muted">Age to</Label>
                <Input type="number" value={filters.age_max} onChange={(e) => upd("age_max", e.target.value)} data-testid="filter-age-max" className="mt-1.5 h-10 rounded-none border-brand-line" placeholder="45" />
              </div>
            </div>

            <div>
              <Label className="text-xs text-brand-muted">Sect</Label>
              <Select value={filters.community} onValueChange={(v) => upd("community", v === "any" ? "" : v)}>
                <SelectTrigger data-testid="filter-community" className="mt-1.5 h-10 rounded-none border-brand-line"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  {SECTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs text-brand-muted">Mother tongue</Label>
              <Select value={filters.mother_tongue} onValueChange={(v) => upd("mother_tongue", v === "any" ? "" : v)}>
                <SelectTrigger data-testid="filter-mtongue" className="mt-1.5 h-10 rounded-none border-brand-line"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  {MOTHER_TONGUES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs text-brand-muted">City</Label>
              <Input value={filters.city} onChange={(e) => upd("city", e.target.value)} data-testid="filter-city" className="mt-1.5 h-10 rounded-none border-brand-line" placeholder="Hyderabad, London…" />
            </div>
            <div>
              <Label className="text-xs text-brand-muted">Profession</Label>
              <Input value={filters.profession} onChange={(e) => upd("profession", e.target.value)} data-testid="filter-profession" className="mt-1.5 h-10 rounded-none border-brand-line" placeholder="Doctor, Engineer…" />
            </div>
            <div>
              <Label className="text-xs text-brand-muted">Marital status</Label>
              <Select value={filters.marital_status} onValueChange={(v) => upd("marital_status", v === "any" ? "" : v)}>
                <SelectTrigger data-testid="filter-marital" className="mt-1.5 h-10 rounded-none border-brand-line"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  <SelectItem value="never_married">Never married</SelectItem>
                  <SelectItem value="divorced">Divorced</SelectItem>
                  <SelectItem value="widowed">Widowed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-2 pt-2">
              <Button onClick={apply} data-testid="apply-filters-btn" className="flex-1 rounded-full bg-brand-terracotta hover:bg-brand-terracotta/90 text-white">Apply</Button>
              <Button onClick={reset} variant="outline" data-testid="reset-filters-btn" className="rounded-full border-brand-line">Reset</Button>
            </div>
          </div>
        </aside>

        <div className="col-span-12 lg:col-span-9">
          {loading ? (
            <div className="text-brand-muted">Loading…</div>
          ) : profiles.length === 0 ? (
            <div className="border border-dashed border-brand-line p-16 text-center text-brand-muted" data-testid="browse-empty">
              No profiles match your search. Try loosening filters.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="browse-grid">
              {profiles.map((p) => <ProfileCard key={p.id} profile={p} />)}
            </div>
          )}

          {total > 12 && (
            <div className="mt-10 flex items-center justify-center gap-2">
              <Button variant="outline" disabled={page === 1} onClick={() => load(page - 1)} data-testid="page-prev">Previous</Button>
              <span className="text-sm text-brand-muted">Page <span className="num-lining">{page}</span> of <span className="num-lining">{Math.ceil(total / 12)}</span></span>
              <Button variant="outline" disabled={page * 12 >= total} onClick={() => load(page + 1)} data-testid="page-next">Next</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
